import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { DeepSearchConversation, DeepSearchMessage } from '../types';

interface DeepSearchContextType {
  conversations: DeepSearchConversation[];
  activeConversationId: string | null;
  activeMessages: DeepSearchMessage[];
  isLoading: boolean;
  sendMessage: (content: string) => void;
  startNewConversation: () => void;
  selectConversation: (conversationId: string) => void;
}

export const DeepSearchContext = createContext<DeepSearchContextType>({
  conversations: [],
  activeConversationId: null,
  activeMessages: [],
  isLoading: false,
  sendMessage: () => {},
  startNewConversation: () => {},
  selectConversation: () => {},
});

// PLACEHOLDER: Remove once IDs are generated by the API endpoint
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const DeepSearchProvider = ({ children }: { children: ReactNode }) => {
  const [conversations, setConversations] = useState<DeepSearchConversation[]>(
    []
  );
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const responseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
    };
  }, []);

  const activeMessages =
    conversations.find((c) => c.id === activeConversationId)?.messages ?? [];

  // PLACEHOLDER: Replace with API call to fetch/create a conversation
  const startNewConversation = useCallback(() => {
    const newConversation: DeepSearchConversation = {
      id: generateId(),
      messages: [],
    };
    setConversations((prev) => [...prev, newConversation]);
    setActiveConversationId(newConversation.id);
  }, []);

  // PLACEHOLDER: Replace with API call to fetch conversation messages
  const selectConversation = useCallback(
    (conversationId: string) => {
      const exists = conversations.some((c) => c.id === conversationId);
      if (exists) {
        setActiveConversationId(conversationId);
      }
    },
    [conversations]
  );

  // PLACEHOLDER: Remove once messages come from the API response
  const addMessageToConversation = useCallback(
    (conversationId: string, message: DeepSearchMessage) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, messages: [...c.messages, message] }
            : c
        )
      );
    },
    []
  );

  const sendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || isLoading) {
        return;
      }

      let conversationId = activeConversationId;

      // PLACEHOLDER: Replace with API call that creates a conversation
      // and returns the conversation ID from the endpoint
      if (!conversationId) {
        const newConversation: DeepSearchConversation = {
          id: generateId(),
          messages: [],
        };
        setConversations((prev) => [...prev, newConversation]);
        setActiveConversationId(newConversation.id);
        conversationId = newConversation.id;
      }

      // PLACEHOLDER: Replace with API call to send message to the endpoint.
      // The user message ID and assistant response should come from the API.
      const userMessage: DeepSearchMessage = {
        id: generateId(),
        content: content.trim(),
        role: 'user',
      };

      addMessageToConversation(conversationId, userMessage);
      setIsLoading(true);

      // PLACEHOLDER: Replace this entire block with the actual API call.
      // The endpoint should return the assistant message with its own ID.
      responseTimeoutRef.current = setTimeout(() => {
        const assistantMessage: DeepSearchMessage = {
          id: generateId(),
          content:
            'Deep Search Agent is not yet connected. This is a placeholder response.',
          role: 'assistant',
        };
        addMessageToConversation(conversationId!, assistantMessage);
        setIsLoading(false);
      }, 1500);
    },
    [isLoading, activeConversationId, addMessageToConversation]
  );

  return (
    <DeepSearchContext.Provider
      value={{
        conversations,
        activeConversationId,
        activeMessages,
        isLoading,
        sendMessage,
        startNewConversation,
        selectConversation,
      }}
    >
      {children}
    </DeepSearchContext.Provider>
  );
};
